import { NETWORK_ENDPOINTS, NETWORK_FETCH_PAGE_SIZE, NETWORK_MAX_PAGES } from "../constants/networkEndpoints";

// The endpoints answer HTTP 200 with statusType FAILURE/ERROR for business errors ("already added", ...)
const unwrap = (data) => {
  if (data?.statusType && data.statusType !== "SUCCESS") {
    throw new Error(data.text || "Request failed");
  }
  return data;
};

export const getErrorText = (err, fallback) => {
  const status = err?.response?.status;
  if (err?.response?.data?.text) return err.response.data.text;
  // A bare 401 / 403 has no JSON body, so Axios would only say "Request failed with status code 403"
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You don't have permission to do this.";
  return err?.message || fallback;
};

// Same page request as mobile (page_number / page_size / order_by ASC), repeated until the last page
const fetchAllPages = async (httpClient, url, listKey) => {
  const rows = [];
  for (let page = 1; page <= NETWORK_MAX_PAGES; page += 1) {
    const response = await httpClient.post(url, {
      page_number: page,
      page_size: NETWORK_FETCH_PAGE_SIZE,
      order_by: "ASC",
    });
    const data = unwrap(response.data);
    const batch = data[listKey] || [];
    rows.push(...batch);
    if (batch.length < NETWORK_FETCH_PAGE_SIZE || (data.totalPage && page >= data.totalPage)) break;
  }
  return rows;
};

// A reward failure must never undo or block a successful add (mobile ignores it too)
const hitReward = async (httpClient, url) => {
  try {
    await httpClient.post(url, {});
    return true;
  } catch {
    return false;
  }
};

export const createPartnerNetworkService = (httpClient) => ({
  // ---- partners ----
  listPartners: () => fetchAllPages(httpClient, NETWORK_ENDPOINTS.partners, "networks"),

  searchPartners: async (keyword) => {
    const response = await httpClient.post(NETWORK_ENDPOINTS.searchUsers, {
      page_number: 1,
      page_size: 200,
      order_by: "ASC",
      keyword_search: keyword,
      user_type: "PARTNER",
    });
    return unwrap(response.data).users || [];
  },

  // Payload shape is the mobile one: the address fields are always sent empty
  addPartner: async ({ first_name, last_name, mobile_number, email_address, company, specialization, notes }) => {
    const response = await httpClient.post(NETWORK_ENDPOINTS.addPartner, {
      first_name,
      last_name,
      mobile_number,
      email_address,
      company,
      specialization,
      notes,
      address: "",
      address1: "",
      street: "",
      apartment: "",
      country_name: "",
      state_name: "",
      city_name: "",
      zip_code: "",
      area_name: "",
    });
    return unwrap(response.data);
  },

  // Mobile edits the four contact fields and sends the saved location back unchanged
  editPartner: async (partner, { first_name, last_name, mobile_number, email_address, company, specialization, notes }) => {
    const response = await httpClient.put(NETWORK_ENDPOINTS.editPartner, {
      id: Number(partner.id),
      country_name: partner.country_name || "",
      state_name: partner.state_name || "",
      city_name: partner.city_name || "",
      area_name: "",
      first_name,
      last_name,
      mobile_number,
      email_address,
      company,
      specialization,
      notes,
    });
    return unwrap(response.data);
  },

  removePartner: async (id) => unwrap((await httpClient.delete(NETWORK_ENDPOINTS.removePartner(id))).data),

  rewardAddPartner: () => hitReward(httpClient, NETWORK_ENDPOINTS.rewardAddPartner),

  // ---- clients (contacts) ----
  listClients: () => fetchAllPages(httpClient, NETWORK_ENDPOINTS.clients, "contacts"),

  addClient: async ({ first_name, last_name, mobile_number, email_address, area_name, country_name, state_name, city_name, zip_code, client_type, budget, notes }) => {
    const response = await httpClient.post(NETWORK_ENDPOINTS.addClient, {
      first_name,
      last_name,
      mobile_number,
      email_address,
      area_name,
      country_name,
      state_name,
      city_name,
      zip_code,
      client_type,
      budget,
      notes,
    });
    return unwrap(response.data);
  },

  // Same fields as add, plus the id of the contact being edited
  editClient: async (client, { first_name, last_name, mobile_number, email_address, area_name, country_name, state_name, city_name, zip_code, client_type, budget, notes }) => {
    const response = await httpClient.put(NETWORK_ENDPOINTS.editClient, {
      id: Number(client.id),
      first_name,
      last_name,
      mobile_number,
      email_address,
      area_name,
      country_name,
      state_name,
      city_name,
      zip_code,
      client_type,
      budget,
      notes,
    });
    return unwrap(response.data);
  },

  removeClient: async (id) => unwrap((await httpClient.delete(NETWORK_ENDPOINTS.removeClient(id))).data),

  rewardAddClient: () => hitReward(httpClient, NETWORK_ENDPOINTS.rewardAddClient),

  // ---- location pickers (same endpoints as mobile) ----
  fetchCountries: async () => {
    const response = await httpClient.post(NETWORK_ENDPOINTS.countries, { page_number: 1, page_size: 100, order_by: "ASC" });
    return response.data?.countries || [];
  },

  fetchStates: async (countryId) => {
    const response = await httpClient.post(NETWORK_ENDPOINTS.states(countryId), { page_number: 1, page_size: 100, order_by: "ASC" });
    return response.data?.states || [];
  },

  fetchCities: async (stateId) => {
    const response = await httpClient.post(NETWORK_ENDPOINTS.cities(stateId), { page_number: 1, page_size: 1000, order_by: "ASC" });
    return response.data?.cities || [];
  },
});
